import React, { useState, useEffect, useContext } from "react"
import PropTypes from "prop-types"
import { compose } from "recompose"

import {
  Box,
  Text,
  VStack,
  Spinner,
  Image,
  Link,
  Divider,
  ChakraProvider,
} from "@chakra-ui/react"
import { withFirebase } from "../../utils/Firebase"
import { withAuthorization, AuthUserContext } from "../../context/Session"
import Nav from "../../components/atoms/Nav"
import * as ROLES from "../../utils/Constants/Roles"

const AdminNotificationHistory = ({ firebase }) => {
  const authUser = useContext(AuthUserContext)
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await firebase.getAdminNotifications(authUser.uid)
        setNotifications(data)
      } catch (error) {
        console.error("Error fetching notifications:", error)
      } finally {
        setLoading(false)
      }
    }

    if (authUser?.uid) {
      fetchNotifications()
    }
  }, [authUser, firebase])

  if (loading) {
    return (
      <ChakraProvider>
        <Nav
          titleKey="notification_history"
          descriptionKey="notifications_sent_desc"
        />
        <Box p={6} maxW="800px" mx="auto">
          <Spinner />
        </Box>
      </ChakraProvider>
    )
  }

  if (notifications.length === 0) {
    return (
      <ChakraProvider>
        <Nav
          titleKey="notification_history"
          descriptionKey="notifications_sent_desc"
        />
        <Box p={6} maxW="800px" mx="auto">
          <Text>No hay notificaciones enviadas aún.</Text>
        </Box>
      </ChakraProvider>
    )
  }

  return (
    <ChakraProvider>
      <Nav
        titleKey="notification_history"
        descriptionKey="notifications_admin_desc"
      />

      <Box p={6} maxW="800px" mx="auto">
        <VStack spacing={6} align="stretch">
          {notifications.map((notif) => (
            <Box
              key={notif.id}
              p={4}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              bg="gray.50"
            >
              <Text fontWeight="bold" fontSize="lg">
                {notif.title}
              </Text>
              <Text>{notif.body}</Text>
              {notif.imageUrl && (
                <Image
                  src={notif.imageUrl}
                  alt="Imagen"
                  mt={5}
                  mb={10}
                  maxH="150px"
                  objectFit="cover"
                  borderRadius="md"
                />
              )}
              {notif.link && (
                <Link href={notif.link} color="blue.500" isExternal mt={2}>
                  link url: {notif.link}
                </Link>
              )}
              <Text mt={2} fontSize="sm" color="gray.600">
                Enviado por: {notif.sentBy?.email || "Desconocido"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Fecha: {notif.sentAt?.toDate().toLocaleString() || "-"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Recipientes: {notif.recipientCount}
              </Text>
              <Divider mt={3} />
            </Box>
          ))}
        </VStack>
      </Box>
    </ChakraProvider>
  )
}

AdminNotificationHistory.propTypes = {
  firebase: PropTypes.shape().isRequired,
}

const condition = (authUser) =>
  !!authUser &&
  authUser.userType === "admin" &&
  authUser.roles[ROLES.LOCAL_ADMIN]

const ComposedLocalAdminNotificationHistory = compose(
  withFirebase,
  withAuthorization(condition)
)(AdminNotificationHistory)

export default ComposedLocalAdminNotificationHistory
